import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { sinistresAPI, photosAPI, BASE_URL } from '../services/api';
import { Camera, Save, ArrowLeft, Upload, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import imageCompression from 'browser-image-compression';
import DictationButton from '../components/DictationButton';
import TemplateSuggester from '../components/TemplateSuggester';
import AISinistreModal from '../components/AISinistreModal';

const FastSinistreForm = () => {
    const navigate = useNavigate();
    const [aiModalOpen, setAiModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [createdId, setCreatedId] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [photos, setPhotos] = useState([]);

    const [form, setForm] = useState({
        numero_sinistre: '',
        nom_assure: '',
        nom_tiers: '',
        lieu_visite: '',
        immatriculation: '',
        date_sinistre: new Date().toISOString().split('T')[0],
        statut: 'brouillon'
    });

    // removed auto-generate next number because reference_interne is handled by backend

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleCreateClaim = async () => {
        if (!form.nom_assure) {
            toast.error("Veuillez remplir au moins le nom de l'assuré");
            return;
        }
        setSaving(true);
        try {
            const res = await sinistresAPI.create(form);
            setCreatedId(res.data.data.id);
            toast.success("Sinistré créé ! Vous pouvez maintenant prendre les photos.");
        } catch (err) {
            toast.error(err.response?.data?.message || "Erreur lors de la création");
        } finally {
            setSaving(false);
        }
    };

    const handlePhotoUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const formData = new FormData();
        toast.loading('Compression express en cours...', { id: 'upload' });

        try {
            const compressedFiles = await Promise.all(
                files.map(async (file) => {
                    const options = {
                        maxSizeMB: 1.0,
                        maxWidthOrHeight: 1600,
                        useWebWorker: true,
                        initialQuality: 0.8
                    };
                    try {
                        return await imageCompression(file, options);
                    } catch (error) {
                        console.error('Fast Compression failed', error);
                        return file;
                    }
                })
            );

            compressedFiles.forEach((f, idx) => {
                const finalFile = new File([f], files[idx].name, { type: f.type });
                formData.append('photos', finalFile);
            });

            formData.append('sinistre_id', createdId);
            if (form.numero_sinistre) formData.append('numero_sinistre', form.numero_sinistre);

            setUploadProgress(20);
            const res = await photosAPI.upload(formData, (pe) => {
                if (pe.total) setUploadProgress(Math.round(pe.loaded / pe.total * 100));
            });
            const newPhotos = (Array.isArray(res.data?.data) ? res.data.data : []).map(p => {
                let photoUrl = p.file_path || p.url;
                if (!photoUrl || photoUrl.includes('\\') || (!photoUrl.startsWith('http') && !photoUrl.startsWith('/') && !photoUrl.startsWith('blob:'))) {
                    photoUrl = `${BASE_URL}/uploads/photos/${p.filename}`;
                }
                return { ...p, url: photoUrl };
            });
            setPhotos(prev => [...(Array.isArray(prev) ? prev : []), ...newPhotos]);
            toast.success(`${files.length} photo(s) ajoutée(s)`, { id: 'upload' });
        } catch (err) {
            toast.error(err.response?.data?.message || "Erreur upload", { id: 'upload' });
        } finally {
            setUploadProgress(0);
        }
    };

    return (
        <Layout title="Création Rapide Mobile">
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <button className="btn btn-ghost mb-4" onClick={() => navigate('/')}>
                    <ArrowLeft size={16} /> Retour Dashboard
                </button>

                {!createdId ? (
                    <div className="card" style={{ padding: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ fontSize: '18px', fontWeight: '800', color: 'var(--primary-800)', margin: 0 }}>
                                ⚡ Express
                            </h2>
                            <button 
                                onClick={() => setAiModalOpen(true)}
                                style={{ 
                                    padding: '10px 16px', 
                                    fontSize: '14px', 
                                    fontWeight: '800',
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px',
                                    background: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '50px',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 15px rgba(255, 140, 0, 0.4)',
                                    transition: 'transform 0.2s',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <Camera size={18} /> ✨ IA SCAN
                            </button>
                        </div>

                        <div className="form-group">
                            <label className="form-label">N° Sin.Assur</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Numéro fourni par l'assurance"
                                value={form.numero_sinistre}
                                onChange={(e) => handleChange('numero_sinistre', e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Nom de l'Assuré *</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Ex: Jean Dupont"
                                value={form.nom_assure}
                                onChange={(e) => handleChange('nom_assure', e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Nom du Tiers</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Ex: Ahmed B."
                                value={form.nom_tiers}
                                onChange={(e) => handleChange('nom_tiers', e.target.value)}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <label className="form-label" style={{ margin: 0 }}>Lieu de visite</label>
                                <DictationButton onTranscript={(text) => handleChange('lieu_visite', (form.lieu_visite ? form.lieu_visite + ' ' : '') + text)} />
                            </div>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Ex: Garage El Yakout, Agadir"
                                value={form.lieu_visite}
                                onChange={(e) => handleChange('lieu_visite', e.target.value)}
                            />
                            <TemplateSuggester
                                field="lieu_visite"
                                value={form.lieu_visite}
                                onSelect={(val) => handleChange('lieu_visite', val)}
                            />
                        </div>

                        <div className="grid-2 gap-3">
                            <div className="form-group">
                                <label className="form-label">Matricule</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="000123-120-16"
                                    value={form.immatriculation}
                                    onChange={(e) => handleChange('immatriculation', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Date de l'accident</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={form.date_sinistre}
                                    onChange={(e) => handleChange('date_sinistre', e.target.value)}
                                    style={{ minWidth: '0' }}
                                />
                            </div>
                        </div>

                        <button
                            className="btn btn-primary w-full"
                            style={{ marginTop: '20px', padding: '16px', fontSize: '16px' }}
                            onClick={handleCreateClaim}
                            disabled={saving}
                        >
                            {saving ? 'Création...' : <><Save size={20} /> Créer le sinistré et continuer</>}
                        </button>
                    </div>
                ) : (
                    <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
                        <div style={{ color: 'var(--success)', marginBottom: '16px' }}>
                            <CheckCircle size={48} style={{ margin: '0 auto' }} />
                            <h2 style={{ fontSize: '20px', fontWeight: '800', marginTop: '12px' }}>Sinistré Créé</h2>
                            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Prêt pour les photos</p>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label className="btn btn-gold w-full" style={{ padding: '20px', fontSize: '18px', cursor: 'pointer', marginBottom: '12px' }}>
                                <Camera size={24} strokeWidth={3} /> PRENDRE UNE PHOTO
                                <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    style={{ display: 'none' }}
                                    onChange={handlePhotoUpload}
                                />
                            </label>

                            <label className="btn btn-outline w-full" style={{ padding: '16px', cursor: 'pointer' }}>
                                <Upload size={20} /> Choisir depuis la galerie
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={handlePhotoUpload}
                                />
                            </label>
                        </div>

                        {uploadProgress > 0 && (
                            <div style={{ background: 'var(--surface-2)', borderRadius: '10px', height: '10px', overflow: 'hidden', marginBottom: '20px' }}>
                                <div style={{ height: '100%', background: 'var(--primary-500)', width: `${uploadProgress}%`, transition: 'width 0.3s' }} />
                            </div>
                        )}

                        <div className="photos-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '24px' }}>
                            {photos.map((p, i) => (
                                <div key={i} className="photo-item" style={{ aspectRatio: '1', borderRadius: '8px', overflow: 'hidden' }}>
                                    <img src={p.url} alt="cap" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                            ))}
                        </div>

                        <button
                            className="btn btn-primary w-full"
                            style={{ padding: '16px' }}
                            onClick={() => navigate(`/sinistres/${createdId}`)}
                        >
                            Terminer et voir le sinistré
                        </button>
                    </div>
                )}
            </div>
            
            <AISinistreModal 
                open={aiModalOpen} 
                onClose={() => setAiModalOpen(false)} 
                onScanned={(data) => {
                    setAiModalOpen(false);
                    navigate('/sinistres/nouveau', { state: data });
                }}
            />
        </Layout>
    );
};

export default FastSinistreForm;
